import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Veterans() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center mt-16">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/e7605311edff4188dbd664db0f9809d3/1fe3c15d92ab9435ca4f4ef1ff5159ff.png"
            alt="Ukrainian veteran looking toward horizon"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
        </div>

        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">{t('veterans_hero_title')}</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            {t('veterans_hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <h3 className="font-semibold text-stone-800 mb-6">{t('veterans_sidebar_title')}</h3>
                <nav className="space-y-4">
                  <a href="#what-we-offer" className="block text-stone-600 hover:text-amber-600 transition-colors duration-200">{t('veterans_what_we_offer')}</a>
                  <a href="#how-it-works" className="block text-stone-600 hover:text-amber-600 transition-colors duration-200">{t('veterans_how_it_works')}</a>
                  <a href="#your-privacy" className="block text-stone-600 hover:text-amber-600 transition-colors duration-200">{t('veterans_your_privacy')}</a>
                  <a href="#get-started" className="block text-stone-600 hover:text-amber-600 transition-colors duration-200">{t('veterans_get_started')}</a>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">
              
              {/* What We Offer */}
              <div id="what-we-offer">
                <h2 className="text-2xl font-semibold text-stone-800 mb-6">{t('veterans_offer_title')}</h2>
                <div className="prose prose-lg prose-stone max-w-none">
                  <p className="text-stone-700 leading-relaxed">
                    {t('veterans_offer_p1')}
                  </p>
                  <p className="text-stone-700 leading-relaxed">
                    {t('veterans_offer_p2')}
                  </p>
                  
                  <div className="bg-stone-50 p-6 rounded-lg border-l-4 border-amber-500 my-8">
                    <p className="text-stone-700 italic mb-0">
                      "{t('veterans_offer_quote')}"
                    </p>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div id="how-it-works">
                <h2 className="text-2xl font-semibold text-stone-800 mb-6">{t('veterans_how_title')}</h2>
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">1</div>
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-2">{t('veterans_step1_title')}</h3>
                      <p className="text-stone-700 leading-relaxed">
                        {t('veterans_step1_desc')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">2</div>
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-2">{t('veterans_step2_title')}</h3>
                      <p className="text-stone-700 leading-relaxed">
                        {t('veterans_step2_desc')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">3</div>
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-2">{t('veterans_step3_title')}</h3>
                      <p className="text-stone-700 leading-relaxed">
                        {t('veterans_step3_desc')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">4</div>
                    <div>
                      <h3 className="font-semibold text-stone-800 mb-2">{t('veterans_step4_title')}</h3>
                      <p className="text-stone-700 leading-relaxed">
                        {t('veterans_step4_desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy */}
              <div id="your-privacy">
                <h2 className="text-2xl font-semibold text-stone-800 mb-6">{t('veterans_privacy_title')}</h2>
                <p className="text-stone-700 leading-relaxed mb-4">
                  {t('veterans_privacy_p1')}
                </p>
                <p className="text-stone-700 leading-relaxed">
                  {t('veterans_privacy_p2')}
                </p>
              </div>

              {/* Get Started */}
              <div id="get-started">
                <h2 className="text-2xl font-semibold text-stone-800 mb-6">{t('veterans_start_title')}</h2>
                <p className="text-stone-700 leading-relaxed mb-8">
                  {t('veterans_start_p1')}
                </p>
                
                <div className="bg-stone-50 rounded-lg p-8 text-center">
                  <h3 className="text-xl font-semibold text-stone-800 mb-4">{t('veterans_cta_title')}</h3>
                  <p className="text-stone-600 mb-6">{t('veterans_cta_subtitle')}</p>
                  <a
                    href="/contact"
                    className="inline-block bg-stone-800 text-white px-8 py-3 rounded-full font-medium hover:bg-amber-600 transition-colors duration-300 whitespace-nowrap"
                  >
                    {t('veterans_cta_button')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}